import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Plus, ListTree } from "lucide-react";

export default function DimensionsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/dimensions");
      setItems(data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (d) => {
    try {
      if (d.active) await api.delete(`/tenant/dimensions/${d.id}`);
      else await api.patch(`/tenant/dimensions/${d.id}`, { active: true });
      toast.success(d.active ? "Dimension disabled" : "Dimension re-activated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="dimensions-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administration</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Dimensions</h1>
        <p className="mt-2 text-muted-foreground">Custom labels attached to time entries — jobs, cost centers, tasks, phases. Required dimensions must be picked at clock-in.</p>
      </div>

      <div><NewDimension onDone={load} /></div>

      <Card>
        <CardHeader><CardTitle>All dimensions</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Values</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && items.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No dimensions yet.</TableCell></TableRow>}
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`dim-row-${d.key}`}>
                  <TableCell><Badge variant="secondary" className="rounded-full font-mono text-xs">{d.key}</Badge></TableCell>
                  <TableCell className="font-medium">{d.label}</TableCell>
                  <TableCell>{d.required ? <Badge className="bg-warning/15 text-warning-foreground rounded-full">Required</Badge> : <span className="text-muted-foreground">Optional</span>}</TableCell>
                  <TableCell>{d.values_count}</TableCell>
                  <TableCell><Badge variant={d.active ? "default" : "outline"} className="capitalize">{d.active ? "active" : "inactive"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button asChild size="sm" variant="ghost"><Link to={`/app/admin/dimensions/${d.id}`}>Values</Link></Button>
                      <Button size="sm" variant="outline" onClick={() => toggle(d)} data-testid={`toggle-dim-${d.key}`}>{d.active ? "Disable" : "Enable"}</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewDimension({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ key: "", label: "", required: false, sort_order: 10 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/tenant/dimensions", { ...f, key: f.key.trim().toLowerCase() });
      toast.success("Dimension created");
      setOpen(false); setF({ key: "", label: "", required: false, sort_order: 10 });
      onDone();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-dim-btn"><Plus className="h-4 w-4" /> New dimension</Button>
      </DialogTrigger>
      <DialogContent data-testid="new-dim-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ListTree className="h-5 w-5" /> New dimension</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Key</Label><Input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} placeholder="task" data-testid="dim-key" /></div>
          <div className="space-y-2"><Label>Label</Label><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Task" data-testid="dim-label" /></div>
          <label className="flex items-center gap-2 rounded-lg border p-3 text-sm" data-testid="dim-required">
            <Checkbox checked={f.required} onCheckedChange={(v) => setF({ ...f, required: !!v })} />
            Required at clock-in
          </label>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !f.key || !f.label} data-testid="dim-save-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
